/* ============================================================================
 * Pawn tutorial — block renderers
 * ----------------------------------------------------------------------------
 * One component per block type in tutorialContent.js. Kept apart from the screen
 * so the walkthrough's chrome (navigator, progress, prev/next) and its prose
 * presentation can change independently.
 *
 * Everything here is presentational and takes only its own block — no context,
 * no data fetching.
 * ========================================================================== */

import React from "react";
import { Box, Typography, Chip, Divider } from "@mui/material";
import {
  InfoRounded as InfoIcon,
  LightbulbRounded as TipIcon,
  WarningRounded as WarnIcon,
  ArrowForwardRounded as ArrowIcon,
} from "@mui/icons-material";

// ─── Blue/White design tokens (same palette as every other pawn screen) ──────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BG_ALT = "#F1F5F9";
const AMBER = "#B45309";
const AMBER_PALE = "#FFFBEB";
const GREEN = "#15803D";
const GREEN_PALE = "#F0FDF4";

/* ── Inline rich text ────────────────────────────────────────────────────────
 * The content is prose, so it needs emphasis without being HTML: `**bold**` for
 * the phrase that carries the point, and `` `code` `` for a literal the user
 * will see on screen (a voucher number, a field name, a formula). Deliberately
 * tiny — anything more and the copy stops being editable by a non-programmer. */
export const Rich = ({ text, sx }) => {
  if (!text) return null;
  // Split on **bold** and `code` in one pass, keeping the delimiters.
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <Box
              key={i}
              component="strong"
              sx={{ fontWeight: 700, color: INK, ...sx }}
            >
              {part.slice(2, -2)}
            </Box>
          );
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          return (
            <Box
              key={i}
              component="code"
              sx={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.86em",
                fontWeight: 700,
                color: BLUE_DARK,
                bgcolor: BLUE_PALE,
                border: `1px solid ${BORDER}`,
                borderRadius: "5px",
                px: 0.5,
                py: "1px",
                mx: "1px",
                whiteSpace: "nowrap",
              }}
            >
              {part.slice(1, -1)}
            </Box>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

// ─── h: a sub-heading inside a chapter ───────────────────────────────────────
const Heading = ({ text }) => (
  <Box sx={{ mt: 3.5, mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
    <Box
      sx={{
        width: 4,
        height: 18,
        bgcolor: BLUE,
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: { xs: "0.95rem", sm: "1.02rem" },
        color: INK,
        letterSpacing: "-0.2px",
      }}
    >
      {text}
    </Typography>
  </Box>
);

// ─── para ────────────────────────────────────────────────────────────────────
const Para = ({ text }) => (
  <Typography
    sx={{
      fontSize: { xs: "0.875rem", sm: "0.9rem" },
      lineHeight: 1.75,
      color: INK_SOFT,
      mb: 1.75,
    }}
  >
    <Rich text={text} />
  </Typography>
);

// ─── bullets ─────────────────────────────────────────────────────────────────
const Bullets = ({ items = [] }) => (
  <Box component="ul" sx={{ m: 0, mb: 2, pl: 0, listStyle: "none" }}>
    {items.map((item, i) => (
      <Box
        component="li"
        key={i}
        sx={{ display: "flex", gap: 1.25, mb: 1, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: BLUE,
            flexShrink: 0,
            mt: "8px",
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: "0.86rem", sm: "0.89rem" },
            lineHeight: 1.7,
            color: INK_SOFT,
          }}
        >
          <Rich text={item} />
        </Typography>
      </Box>
    ))}
  </Box>
);

// ─── steps: numbered, for "do this then this" ────────────────────────────────
const Steps = ({ items = [] }) => (
  <Box sx={{ mb: 2.25 }}>
    {items.map((step, i) => (
      <Box
        key={i}
        sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "8px",
            bgcolor: BLUE,
            color: "#fff",
            fontSize: "0.72rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: "1px",
          }}
        >
          {i + 1}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: "0.89rem", fontWeight: 700, color: INK, lineHeight: 1.5 }}
          >
            {step.label}
          </Typography>
          {step.text && (
            <Typography
              sx={{
                fontSize: "0.85rem",
                lineHeight: 1.65,
                color: INK_MUTED,
                mt: 0.25,
              }}
            >
              <Rich text={step.text} />
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

// ─── fields: the field-by-field reference ────────────────────────────────────
const Fields = ({ items = [] }) => (
  <Box
    sx={{
      mb: 2.25,
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    {items.map((f, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 0.25, sm: 2 },
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
          bgcolor: i % 2 ? BG_ALT : "#fff",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: 190 },
            flexShrink: 0,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              color: BLUE_DARK,
              lineHeight: 1.5,
            }}
          >
            {f.name}
          </Typography>
          {f.tag && (
            <Chip
              label={f.tag}
              size="small"
              sx={{
                height: 17,
                fontSize: "0.6rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                bgcolor: BLUE_PALE,
                color: BLUE_DARK,
                border: `1px solid ${BORDER}`,
                "& .MuiChip-label": { px: 0.6 },
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            fontSize: "0.85rem",
            lineHeight: 1.68,
            color: INK_SOFT,
            minWidth: 0,
          }}
        >
          <Rich text={f.text} />
        </Typography>
      </Box>
    ))}
  </Box>
);

// ─── example: a worked calculation in rupees ─────────────────────────────────
// The whole point of the tutorial, so it gets the most deliberate presentation:
// a titled card, the inputs as label/value rows, and the answer set apart.
const Example = ({ title, subtitle, rows = [], total, foot }) => (
  <Box
    sx={{
      mb: 2.5,
      border: `1.5px solid ${BLUE}`,
      borderRadius: "12px",
      overflow: "hidden",
      bgcolor: "#fff",
    }}
  >
    <Box
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
        bgcolor: BLUE_PALE,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.6rem",
          fontWeight: 700,
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          color: BLUE,
          mb: 0.25,
        }}
      >
        Worked example
      </Typography>
      <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: INK }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{ fontSize: "0.76rem", color: INK_MUTED, mt: 0.25, lineHeight: 1.5 }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>

    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 0.5 }}>
      {rows.map((r, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
            py: 1,
            borderBottom: i === rows.length - 1 ? "none" : `1px dashed ${BORDER}`,
          }}
        >
          <Typography
            sx={{ fontSize: "0.85rem", color: INK_SOFT, minWidth: 0, flex: 1 }}
          >
            <Rich text={r.label} />
            {r.note && (
              <Typography
                component="span"
                sx={{
                  display: "block",
                  fontSize: "0.73rem",
                  color: INK_MUTED,
                  fontStyle: "italic",
                  mt: 0.15,
                }}
              >
                {r.note}
              </Typography>
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 700,
              color: INK,
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {r.value}
          </Typography>
        </Box>
      ))}
    </Box>

    {total && (
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          bgcolor: BLUE,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: 700,
            color: "rgba(255,255,255,0.9)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {total.label}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.05rem",
            fontWeight: 800,
            color: "#fff",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {total.value}
        </Typography>
      </Box>
    )}

    {foot && (
      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, bgcolor: BG_ALT }}>
        <Typography sx={{ fontSize: "0.79rem", lineHeight: 1.65, color: INK_MUTED }}>
          <Rich text={foot} />
        </Typography>
      </Box>
    )}
  </Box>
);

// ─── table: small comparison grids ───────────────────────────────────────────
// Scrolls inside itself on a phone rather than widening the page — the same rule
// the report screens follow.
const SimpleTable = ({ head = [], rows = [], foot }) => (
  <Box sx={{ mb: 2.25 }}>
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Box
        component="table"
        sx={{
          width: "100%",
          minWidth: head.length > 3 ? 560 : 380,
          borderCollapse: "collapse",
        }}
      >
        <Box component="thead">
          <Box component="tr" sx={{ bgcolor: BG_ALT }}>
            {head.map((h, i) => (
              <Box
                component="th"
                key={i}
                sx={{
                  textAlign: "left",
                  px: 1.5,
                  py: 1,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: INK_MUTED,
                  borderBottom: `1px solid ${BORDER}`,
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((r, ri) => (
            <Box component="tr" key={ri}>
              {r.map((c, ci) => (
                <Box
                  component="td"
                  key={ci}
                  sx={{
                    px: 1.5,
                    py: 1.1,
                    fontSize: "0.83rem",
                    lineHeight: 1.6,
                    color: ci === 0 ? INK : INK_SOFT,
                    fontWeight: ci === 0 ? 700 : 500,
                    borderBottom:
                      ri === rows.length - 1 ? "none" : `1px solid ${BORDER}`,
                    verticalAlign: "top",
                  }}
                >
                  <Rich text={c} />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
    {foot && (
      <Typography
        sx={{
          fontSize: "0.77rem",
          color: INK_MUTED,
          mt: 0.75,
          lineHeight: 1.6,
          fontStyle: "italic",
        }}
      >
        <Rich text={foot} />
      </Typography>
    )}
  </Box>
);

// ─── callout: info / tip / warn ──────────────────────────────────────────────
const TONES = {
  info: { color: BLUE_DARK, bg: BLUE_PALE, border: BLUE, Icon: InfoIcon },
  tip: { color: GREEN, bg: GREEN_PALE, border: GREEN, Icon: TipIcon },
  warn: { color: AMBER, bg: AMBER_PALE, border: AMBER, Icon: WarnIcon },
};

const Callout = ({ tone = "info", title, text }) => {
  const t = TONES[tone] || TONES.info;
  const { Icon } = t;
  return (
    <Box
      sx={{
        mb: 2.25,
        display: "flex",
        gap: 1.25,
        p: { xs: 1.25, sm: 1.75 },
        borderRadius: "12px",
        bgcolor: t.bg,
        border: `1px solid ${t.border}33`,
        borderLeft: `3px solid ${t.border}`,
      }}
    >
      <Icon sx={{ fontSize: 19, color: t.color, flexShrink: 0, mt: "1px" }} />
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              color: t.color,
              mb: 0.35,
            }}
          >
            {title}
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.7, color: INK_SOFT }}>
          <Rich text={text} />
        </Typography>
      </Box>
    </Box>
  );
};

// ─── flow: left-to-right stages (wraps to a column on a phone) ───────────────
const Flow = ({ items = [] }) => (
  <Box
    sx={{
      mb: 2.5,
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        lg: `repeat(${Math.min(items.length, 4)}, 1fr)`,
      },
      gap: 1.25,
    }}
  >
    {items.map((s, i) => (
      <Box
        key={i}
        sx={{
          position: "relative",
          p: 1.5,
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "6px",
              bgcolor: BLUE_PALE,
              color: BLUE_DARK,
              fontSize: "0.66rem",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {i + 1}
          </Box>
          <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: INK }}>
            {s.label}
          </Typography>
          {i < items.length - 1 && (
            <ArrowIcon
              sx={{
                fontSize: 15,
                color: BLUE,
                ml: "auto",
                display: { xs: "none", lg: "block" },
              }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: "0.79rem", lineHeight: 1.6, color: INK_MUTED }}>
          <Rich text={s.text} />
        </Typography>
      </Box>
    ))}
  </Box>
);

// ─── kbd: keyboard / scanner shortcuts ───────────────────────────────────────
const Keys = ({ items = [] }) => (
  <Box sx={{ mb: 2.25 }}>
    {items.map((k, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          gap: 1.25,
          py: 0.85,
          borderBottom: i === items.length - 1 ? "none" : `1px dashed ${BORDER}`,
        }}
      >
        <Box
          sx={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.76rem",
            fontWeight: 700,
            color: INK,
            bgcolor: BG_ALT,
            border: `1px solid ${BORDER}`,
            borderBottomWidth: 2,
            borderRadius: "6px",
            px: 0.85,
            py: 0.3,
            minWidth: 74,
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          {k.keys}
        </Box>
        <Typography
          sx={{ fontSize: "0.85rem", color: INK_SOFT, lineHeight: 1.6, flex: 1, minWidth: 0 }}
        >
          <Rich text={k.text} />
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * Renders one block. Unknown types render nothing rather than throwing — a typo
 * in the content file should cost a paragraph, not the whole screen.
 */
const TutorialBlock = ({ block }) => {
  if (!block || !block.type) return null;
  switch (block.type) {
    case "h":
      return <Heading text={block.text} />;
    case "para":
      return <Para text={block.text} />;
    case "bullets":
      return <Bullets items={block.items} />;
    case "steps":
      return <Steps items={block.items} />;
    case "fields":
      return <Fields items={block.items} />;
    case "example":
      return (
        <Example
          title={block.title}
          subtitle={block.subtitle}
          rows={block.rows}
          total={block.total}
          foot={block.foot}
        />
      );
    case "table":
      return <SimpleTable head={block.head} rows={block.rows} foot={block.foot} />;
    case "callout":
      return <Callout tone={block.tone} title={block.title} text={block.text} />;
    case "flow":
      return <Flow items={block.items} />;
    case "kbd":
      return <Keys items={block.items} />;
    case "divider":
      return <Divider sx={{ my: 2.5, borderColor: BORDER }} />;
    default:
      return null;
  }
};

export default TutorialBlock;
